import {
  SITE_URL,
  BRAND,
  LEGAL_NAME,
  LOGO_URL,
  CVR,
  APP_STORE_URL,
  PLAY_STORE_URL,
  absoluteUrl,
} from "./siteConfig";

/**
 * JSON-LD builders.
 *
 * Ground rules, deliberately conservative:
 *  - Only emit a type when the page genuinely is that thing.
 *  - Never emit a property we cannot substantiate from real data. In
 *    particular we do NOT emit `offers`/`price` on dish pages (prices are only
 *    shown to signed-in users, so advertising them in structured data would
 *    not match the visible page) and we do NOT emit `NutritionInformation`
 *    (the API returns bare numbers with no units, so any unit we added would
 *    be invented).
 *  - Organization rather than LocalBusiness: we have a CVR, phone and email,
 *    but no street address, and LocalBusiness without a real address is worse
 *    than no markup at all.
 */

const ID = {
  organization: `${SITE_URL}/#organization`,
  website: `${SITE_URL}/#website`,
};

export function organizationSchema(contact = {}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ID.organization,
    name: BRAND,
    legalName: LEGAL_NAME,
    url: `${SITE_URL}/`,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
    },
    taxID: CVR,
    areaServed: {
      "@type": "Country",
      name: "Denmark",
    },
  };

  const contactPoint = {};
  if (contact.email) contactPoint.email = contact.email;
  if (contact.phone) contactPoint.telephone = contact.phone;
  if (Object.keys(contactPoint).length) {
    schema.contactPoint = [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        availableLanguage: ["da"],
        ...contactPoint,
      },
    ];
  }

  const sameAs = [APP_STORE_URL, PLAY_STORE_URL].filter(Boolean);
  if (sameAs.length) schema.sameAs = sameAs;

  return schema;
}

export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": ID.website,
    url: `${SITE_URL}/`,
    name: BRAND,
    inLanguage: "da-DK",
    publisher: { "@id": ID.organization },
  };
}

export function webPageSchema({ title, description, url, image }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    url: absoluteUrl(url),
    inLanguage: "da-DK",
    isPartOf: { "@id": ID.website },
    publisher: { "@id": ID.organization },
  };
  if (description) schema.description = description;
  if (image) schema.primaryImageOfPage = { "@type": "ImageObject", url: image };
  return schema;
}

// NOTE: breadcrumbSchema() was removed along with the visible breadcrumb trail.
// BreadcrumbList markup is only valid when the page actually shows a breadcrumb
// trail, and the trail was reverted to match the live site's design, so the
// structured data had to go with it. If breadcrumbs are ever reinstated, the
// builder needs to come back at the same time — not one without the other.

/** @param items [{ question, answer }] — answers must be the visible answers. */
export function faqPageSchema(items = []) {
  const valid = items.filter((i) => i && i.question && i.answer);
  if (!valid.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: valid.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

/** @param items [{ name, path }] — used for category grids and menu listings. */
export function itemListSchema({ name, url, items = [] }) {
  if (!items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url: absoluteUrl(url),
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: absoluteUrl(item.path),
    })),
  };
}

/** A single dish. `MenuItem` is the accurate type for an item on a food menu. */
export function menuItemSchema({ name, description, image, url }) {
  if (!name) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "MenuItem",
    name,
    url: absoluteUrl(url),
  };
  if (description) schema.description = description;
  if (image) schema.image = image;
  return schema;
}

/** Used by the location landing pages, which describe a service in an area. */
export function serviceSchema({ name, description, url, areaServed }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    url: absoluteUrl(url),
    serviceType: "Skolemad og frokostordning",
    provider: { "@id": ID.organization },
  };
  if (description) schema.description = description;
  if (areaServed) {
    schema.areaServed = { "@type": "Place", name: areaServed };
  }
  return schema;
}

export default {
  organizationSchema,
  webSiteSchema,
  webPageSchema,
  faqPageSchema,
  itemListSchema,
  menuItemSchema,
  serviceSchema,
};
